import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { viewerDisplayTimeZone } from '../../util/dates';
import {
  listPendingHolds,
  listRecentSales,
  summarizeListenerEarnings,
} from '../../util/listenerEarnings';
import { useConfiguration } from '../../context/configurationContext';

import NamedLink from '../NamedLink/NamedLink';

import css from './ListenerEarnings.module.css';

const { Money } = sdkTypes;

const moneyLabel = (intl, money, fallbackCurrency) => {
  try {
    if (money instanceof Money) {
      return formatMoney(intl, money);
    }
    if (money?.currency) {
      return formatMoney(intl, new Money(money.amount || 0, money.currency));
    }
    return formatMoney(intl, new Money(0, fallbackCurrency));
  } catch (e) {
    return formatMoney(intl, new Money(0, fallbackCurrency));
  }
};

/**
 * Listener earnings from sales: this month, all time, and recent sale lines.
 *
 * @component
 * @param {Object} props
 * @param {Array} [props.sales]
 * @param {string} [props.className]
 * @param {boolean} [props.flush]
 * @param {Date} [props.now]
 * @param {boolean} [props.hideIntro]
 * @returns {JSX.Element}
 */
const ListenerEarnings = props => {
  const { sales = [], className, flush = false, now, hideIntro = false } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const fallbackCurrency = config?.currency || 'USD';
  const summary = summarizeListenerEarnings(sales, now || new Date());
  const recent = listRecentSales(sales);
  const pending = listPendingHolds(sales);
  const weekMoney = summary.week || new Money(0, summary.currency || fallbackCurrency);
  const monthMoney = summary.month || new Money(0, summary.currency || fallbackCurrency);
  const allMoney = summary.allTime || new Money(0, summary.currency || fallbackCurrency);
  const timeZone = viewerDisplayTimeZone();

  return (
    <section
      className={classNames(css.root, { [css.flush]: flush }, className)}
      aria-labelledby={hideIntro ? undefined : 'listener-earnings-heading'}
    >
      {hideIntro ? null : (
        <>
          <h2 id="listener-earnings-heading" className={css.title}>
            <FormattedMessage id="ListenerEarnings.title" />
          </h2>
          <p className={css.lede}>
            <FormattedMessage id="ListenerEarnings.lede" />
          </p>
        </>
      )}
      <div className={css.grid}>
        <div className={css.stat} data-earnings-stat="this-week">
          <p className={css.label}>
            <FormattedMessage id="ListenerEarnings.thisWeek" />
          </p>
          <p className={css.amount}>{moneyLabel(intl, weekMoney, fallbackCurrency)}</p>
          <p className={css.meta}>
            <FormattedMessage
              id="ListenerEarnings.sessionCount"
              values={{ count: summary.weekCount }}
            />
          </p>
        </div>
        <div className={css.stat}>
          <p className={css.label}>
            <FormattedMessage id="ListenerEarnings.thisMonth" />
          </p>
          <p className={css.amount}>{moneyLabel(intl, monthMoney, fallbackCurrency)}</p>
          <p className={css.meta}>
            <FormattedMessage
              id="ListenerEarnings.sessionCount"
              values={{ count: summary.monthCount }}
            />
          </p>
        </div>
        <div className={css.stat} data-earnings-stat="all-time">
          <p className={css.label}>
            <FormattedMessage id="ListenerEarnings.allTime" />
          </p>
          <p className={css.amount}>{moneyLabel(intl, allMoney, fallbackCurrency)}</p>
          <p className={css.meta}>
            <FormattedMessage
              id="ListenerEarnings.sessionCount"
              values={{ count: summary.allCount }}
            />
          </p>
        </div>
      </div>
      {pending.length > 0 ? (
        <div className={css.pending} data-earnings-pending="true">
          <p className={css.pendingLabel}>
            <FormattedMessage id="ListenerEarnings.pending" />
          </p>
          <p className={css.pendingHint}>
            <FormattedMessage id="ListenerEarnings.pendingHint" />
          </p>
          <ul className={css.salesList}>
            {pending.map(sale => {
              const when = sale.when
                ? intl.formatDate(sale.when, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone,
                  })
                : null;
              const name =
                sale.customerName || intl.formatMessage({ id: 'ListenerEarnings.someone' });
              return (
                <li key={sale.id} className={css.saleRow} data-earnings-pending-row={sale.id}>
                  <NamedLink
                    className={css.saleLink}
                    name="SaleDetailsPage"
                    params={{ id: sale.id }}
                  >
                    <span className={css.saleCopy}>
                      <span className={css.saleName}>{name}</span>
                      {when ? <span className={css.saleWhen}>{when}</span> : null}
                    </span>
                    <span className={css.saleAmount}>
                      {moneyLabel(intl, sale.money, fallbackCurrency)}
                    </span>
                  </NamedLink>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      {recent.length > 0 ? (
        <ul className={css.salesList} data-earnings-earned="true">
          {recent.map(sale => {
            const when = sale.when
              ? intl.formatDate(sale.when, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  timeZone,
                })
              : null;
            const name =
              sale.customerName || intl.formatMessage({ id: 'ListenerEarnings.someone' });
            return (
              <li key={sale.id} className={css.saleRow}>
                <NamedLink className={css.saleLink} name="SaleDetailsPage" params={{ id: sale.id }}>
                  <span className={css.saleCopy}>
                    <span className={css.saleName}>{name}</span>
                    {sale.listingTitle ? (
                      <span className={css.saleTitle}>{sale.listingTitle}</span>
                    ) : null}
                    {when ? <span className={css.saleWhen}>{when}</span> : null}
                    {sale.tipMoney ? (
                      <span className={css.saleTip} data-earnings-tip={sale.id}>
                        <FormattedMessage
                          id="ListenerEarnings.tipLine"
                          values={{
                            amount: moneyLabel(intl, sale.tipTotal, fallbackCurrency),
                            earn: moneyLabel(intl, sale.tipMoney, fallbackCurrency),
                          }}
                        />
                      </span>
                    ) : null}
                  </span>
                  <span className={css.saleAmount}>
                    {moneyLabel(intl, sale.money, fallbackCurrency)}
                    {sale.tipMoney ? (
                      <span className={css.saleTipAmount}>
                        <FormattedMessage
                          id="ListenerEarnings.tipPlus"
                          values={{ earn: moneyLabel(intl, sale.tipMoney, fallbackCurrency) }}
                        />
                      </span>
                    ) : null}
                  </span>
                </NamedLink>
              </li>
            );
          })}
        </ul>
      ) : pending.length > 0 ? null : (
        <p className={css.empty}>
          <FormattedMessage id="ListenerEarnings.emptySales" />
        </p>
      )}
      <p className={css.links}>
        <NamedLink className={css.link} name="InboxPage" params={{ tab: 'sales' }}>
          <FormattedMessage id="ListenerEarnings.sales" />
        </NamedLink>
        <NamedLink className={css.link} name="StripePayoutPage" to={{ hash: '#payouts' }}>
          <FormattedMessage id="ListenerEarnings.payouts" />
        </NamedLink>
      </p>
    </section>
  );
};

export default ListenerEarnings;
